import React, { useEffect, useRef } from 'react';

type ItemObserverProps<T> = {
  item: T;
  index: number;
  onVisible: (info: { item: T; index: number }) => void;
  onHidden?: (info: { item: T; index: number }) => void;
  children: React.ReactNode;
};

export const ItemObserver = <T,>({
  item,
  index,
  onVisible,
  onHidden,
  children,
}: ItemObserverProps<T>) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible({ item, index });
        } else if (onHidden) {
          onHidden({ item, index });
        }
      },
      {
        root: null,
        threshold: 0.8, // Adjust based on how much visibility you want to count as "in view"
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [item, index, onVisible, onHidden]);

  return <div id="observer-div" ref={ref}>{children}</div>;
};
